/**
 * Schedule Lines Filters Panel Component.
 *
 * Grouped into labelled sections (Order · Supplier & Warehouse · Status ·
 * Dates) so users can scan the panel faster. Supplier Site uses a
 * datalist-backed typeahead populated from getLineFilterOptions so users
 * see the real supplier names instead of having to free-type.
 *
 * The section shell, grid and field styling come from the app-wide
 * `components/filters/FilterSection` primitives so this panel matches the
 * dashboard, PO Lines, PO Headers and Deliveries panels instead of carrying its
 * own local section/label/control classes. Restyle only — every control keeps
 * the element type, props and handlers it had before.
 */
import { useCallback, useRef, useEffect, useState, useMemo } from 'react';

import {
  ScheduleFilters,
  ACKNOWLEDGE_STATUS_OPTIONS,
  SCHEDULE_STATUS_OPTIONS,
  ERP_ORDER_TYPE_OPTIONS,
} from './types';

import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import {
  FilterSection,
  FilterGrid,
  FilterField,
  filterControlClass,
} from '../../../filters/FilterSection';
import { purchasingAPI, coreAPI } from '../../../../lib/api';
import { useAuth } from '../../../../contexts/AuthContext';
import { Search, Calendar, X, Filter, ChevronDown } from 'lucide-react';

interface ScheduleFiltersPanelProps {
  filters: ScheduleFilters;
  onFilterChange: (key: keyof ScheduleFilters, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onHideFilters?: () => void;
}

/** Date controls carry a calendar glyph, so they need room for it on the left. */
const dateInputCls = `${filterControlClass} pl-9`;

/**
 * Debounced filter panel for schedule lines.
 * Text inputs use 500ms debounce; dropdowns and dates trigger immediately.
 */
export function ScheduleFiltersPanel({
  filters,
  onFilterChange,
  onSearch,
  onClearFilters,
  onHideFilters,
}: ScheduleFiltersPanelProps) {
  const { user } = useAuth();
  // Supplier-part filter only exists for schedule-line (DE-handler) tenants —
  // the ERP publishes supplier part references there.
  const isScheduleTenant = user?.import_handler === 'dennis_eagle';
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [supplierOptions, setSupplierOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  // Warehouses sourced from the ``core.Warehouse`` model (admin-managed),
  // not from a hardcoded plant-derived list. Empty until the customer
  // populates Admin Setup → Warehouses.
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ id: number; name: string }>>([]);

  // Filter supplier list by the current typed value (matches the PO Lines page UX).
  const filteredSuppliers = useMemo(() => {
    const term = (filters.supplier || '').toLowerCase();
    if (!term) return supplierOptions;
    return supplierOptions.filter(s => s.name.toLowerCase().includes(term));
  }, [supplierOptions, filters.supplier]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Fetch supplier list once for the Supplier Site typeahead.
  useEffect(() => {
    let cancelled = false;
    purchasingAPI.getLineFilterOptions()
      .then((res) => {
        if (!cancelled) setSupplierOptions(res.data.suppliers || []);
      })
      .catch(() => { /* non-fatal — user can still type a supplier name */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch warehouse list from the ``core.Warehouse`` model.
  useEffect(() => {
    let cancelled = false;
    // ``accessible`` action — open to all authenticated users and scoped to
    // their sites (the plain list endpoint is Helix-admin only and 403s for
    // regular customer users, leaving the dropdown empty).
    coreAPI.getAccessibleWarehouses()
      .then((res) => {
        if (!cancelled) {
          setWarehouseOptions((res.data || []).map((w) => ({ id: w.id, name: w.name })));
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const handleTextChange = useCallback(
    (key: keyof ScheduleFilters, value: string) => {
      onFilterChange(key, value);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onSearch();
      }, 500);
    },
    [onFilterChange, onSearch]
  );

  const handleSelectChange = useCallback(
    (key: keyof ScheduleFilters, value: string) => {
      onFilterChange(key, value);
      setTimeout(() => onSearch(), 0);
    },
    [onFilterChange, onSearch]
  );

  const handleDateChange = useCallback(
    (key: keyof ScheduleFilters, value: string) => {
      onFilterChange(key, value);
      setTimeout(() => onSearch(), 0);
    },
    [onFilterChange, onSearch]
  );

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-base">Filter Schedule Lines</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-0 pb-4">
        {/* Order identity — narrow the list to a specific PO / line / part */}
        <FilterSection title="Order" bare>
          <FilterGrid>
            <FilterField label="PO Number">
              <input type="text" placeholder="620596..." value={filters.poNumber}
                onChange={(e) => handleTextChange('poNumber', e.target.value)} className={filterControlClass} />
            </FilterField>
            <FilterField label="Line #">
              <input type="text" placeholder="1, 2, 3..." value={filters.lineNumber}
                onChange={(e) => handleTextChange('lineNumber', e.target.value)} className={filterControlClass} />
            </FilterField>
            <FilterField label="Sched Line #">
              <input type="text" placeholder="1, 2, 3..." value={filters.scheduleLineNumber}
                onChange={(e) => handleTextChange('scheduleLineNumber', e.target.value)} className={filterControlClass} />
            </FilterField>
            <FilterField label="Part Number">
              <input type="text" placeholder="Part number..." value={filters.partNumber}
                onChange={(e) => handleTextChange('partNumber', e.target.value)} className={filterControlClass} />
            </FilterField>
            {isScheduleTenant && (
              <FilterField label="Supplier Part No">
                <input type="text" placeholder="Supplier part..." value={filters.supplierPartNumber}
                  onChange={(e) => handleTextChange('supplierPartNumber', e.target.value)} className={filterControlClass} />
              </FilterField>
            )}
            <FilterField label="ERP Order Type">
              <select value={filters.erpOrderType}
                onChange={(e) => handleSelectChange('erpOrderType', e.target.value)} className={filterControlClass}>
                <option value="">All Types</option>
                {ERP_ORDER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FilterField>
          </FilterGrid>
        </FilterSection>

        {/* Supplier & warehouse */}
        <FilterSection title="Supplier & Warehouse" bare>
          <FilterGrid>
            {/* Positioned wrapper so the typeahead menu anchors to this cell. */}
            <div className="relative">
              <FilterField label="Supplier Site">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search supplier sites..."
                    value={filters.supplier}
                    onChange={(e) => {
                      handleTextChange('supplier', e.target.value);
                      setSupplierDropdownOpen(true);
                    }}
                    onFocus={() => setSupplierDropdownOpen(true)}
                    className={`${filterControlClass} pr-12`}
                    autoComplete="off"
                  />
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  {filters.supplier && (
                    <button
                      type="button"
                      onClick={() => {
                        onFilterChange('supplier', '');
                        setTimeout(() => onSearch(), 0);
                      }}
                      className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {supplierDropdownOpen && filteredSuppliers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-auto">
                    {filteredSuppliers.slice(0, 50).map(s => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => {
                          onFilterChange('supplier', s.name);
                          setSupplierDropdownOpen(false);
                          setTimeout(() => onSearch(), 0);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
                {supplierDropdownOpen && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setSupplierDropdownOpen(false)}
                  />
                )}
              </FilterField>
            </div>
            <FilterField label="Warehouse">
              <select value={filters.warehouse}
                onChange={(e) => handleSelectChange('warehouse', e.target.value)} className={filterControlClass}>
                <option value="">All Warehouses</option>
                {warehouseOptions.map((wh) => (
                  <option key={wh.id} value={String(wh.id)}>{wh.name}</option>
                ))}
              </select>
            </FilterField>
          </FilterGrid>
        </FilterSection>

        {/* Status */}
        <FilterSection title="Status" bare>
          <FilterGrid>
            <FilterField label="Ack Status">
              <select value={filters.acknowledgeStatus}
                onChange={(e) => handleSelectChange('acknowledgeStatus', e.target.value)} className={filterControlClass}>
                <option value="">All Statuses</option>
                {ACKNOWLEDGE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Schedule Status">
              <select value={filters.scheduleStatus}
                onChange={(e) => handleSelectChange('scheduleStatus', e.target.value)} className={filterControlClass}>
                <option value="">All Statuses</option>
                {SCHEDULE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="On Hold">
              <select value={filters.onHold}
                onChange={(e) => handleSelectChange('onHold', e.target.value)} className={filterControlClass}>
                <option value="">All</option>
                <option value="yes">On Hold</option>
                <option value="no">Not On Hold</option>
              </select>
            </FilterField>
          </FilterGrid>
        </FilterSection>

        {/* Dates */}
        <FilterSection title="Dates" bare>
          <FilterGrid>
            <FilterField label="Schedule Date From">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.scheduleDateFrom}
                  onChange={(e) => handleDateChange('scheduleDateFrom', e.target.value)}
                  className={dateInputCls} />
              </div>
            </FilterField>
            <FilterField label="Schedule Date To">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.scheduleDateTo}
                  onChange={(e) => handleDateChange('scheduleDateTo', e.target.value)}
                  className={dateInputCls} />
              </div>
            </FilterField>
            <FilterField label="Promise Date From">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.promiseDateFrom}
                  onChange={(e) => handleDateChange('promiseDateFrom', e.target.value)}
                  className={dateInputCls} />
              </div>
            </FilterField>
            <FilterField label="Promise Date To">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.promiseDateTo}
                  onChange={(e) => handleDateChange('promiseDateTo', e.target.value)}
                  className={dateInputCls} />
              </div>
            </FilterField>
          </FilterGrid>
        </FilterSection>

        {/* Search + Clear Buttons */}
        <div className="flex gap-2 pt-3 border-t">
          <Button size="sm" onClick={onSearch}>
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          <Button size="sm" variant="outline" onClick={onClearFilters}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button size="sm" variant="outline" onClick={onHideFilters}>
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Hide
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
